import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
const url = new URL(window.location.href);
const finalUrl = `/api/`;

function UploadModal({
  setShowUploadModal,
  previewUrl,
  setPreviewUrl,
  setAvatarUrl,
}) {
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size cannot exceed 5M');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleReupload = () => {
    setAvatarUrl('');
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/png,image/gif';
    fileInput.onchange = (e) => handleImageUpload(e);
    fileInput.click();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Upload Avatar</h3>
          <button
            onClick={() => setShowUploadModal(false)}
            className="text-gray-400 hover:text-gray-500"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="w-full h-px bg-gray-200 mb-6"></div>
        <div className="mb-6">
          <div className="block w-full h-[200px] bg-gray-50 rounded-lg">
            <div className="w-full h-full flex flex-col items-center justify-center">
              {previewUrl ? (
                <div className="w-36 h-36">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
              ) : (
                <div
                  className="w-full h-full flex flex-col items-center justify-center cursor-pointer"
                  onClick={handleReupload}
                >
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mb-3">
                    <i className="fas fa-plus text-white text-xl"></i>
                  </div>
                  <p className="text-base text-gray-500">Add Image</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Only JPG/PNG/GIF supported, max size 5M
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="text-left mt-4">
            <button
              onClick={handleReupload}
              type="button"
              className={`text-sm ${
                previewUrl
                  ? 'text-red-500 hover:text-red-600 cursor-pointer'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
              disabled={!previewUrl}
            >
              Re-upload
            </button>
          </div>
        </div>
        <div className="flex justify-center gap-8">
          <button
            onClick={() => {
              setShowUploadModal(false);
              setPreviewUrl('');
            }}
            className="px-6 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 !rounded-button whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (previewUrl) {
                setAvatarUrl(previewUrl);
              }
              setShowUploadModal(false);
              setPreviewUrl('');
            }}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 !rounded-button whitespace-nowrap"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Sidebar({ activeTab, setActiveTab }) {
  return (
    <div className="w-80 bg-white">
      <div className="p-6">
        <div className="space-y-6">
          <div
            onClick={() => {
              setActiveTab('personal');
              window.history.pushState({}, '', '?tab=personal');
            }}
            className={`flex items-center space-x-2 cursor-pointer relative before:absolute before:left-[-24px] before:top-0 before:h-full before:w-1 ${
              activeTab === 'personal'
                ? 'text-gray-900 before:bg-red-600'
                : 'text-gray-600 hover:text-red-600 before:bg-transparent hover:before:bg-red-600'
            } transition-all`}
          >
            <span className={activeTab === 'personal' ? 'font-medium' : ''}>
              Personal data
            </span>
          </div>
          <div
            onClick={() => {
              setActiveTab('password');
              window.history.pushState({}, '', '?tab=password');
            }}
            className={`flex items-center space-x-2 cursor-pointer relative before:absolute before:left-[-24px] before:top-0 before:h-full before:w-1 ${
              activeTab === 'password'
                ? 'text-gray-900 before:bg-red-600'
                : 'text-gray-600 hover:text-red-600 before:bg-transparent hover:before:bg-red-600'
            } transition-all`}
          >
            <span className={activeTab === 'password' ? 'font-medium' : ''}>
              Change Password
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Content({
  activeTab,
  handleSubmit,
  isEditing,
  setIsEditing,
  setShowUploadModal,
  formData,
  handleInputChange,
  avatarUrl,
}) {
  return (
    <div className="flex-1 bg-white">
      <div className="p-8">
        <h1 className="text-2xl font-medium mb-8 text-gray-900">
          {activeTab === 'personal'
            ? 'Personal Information'
            : 'Change Password'}
        </h1>
        <div className="w-full h-[2px] bg-gray-200 mb-8"></div>
        <form onSubmit={handleSubmit} className="space-y-8">
          {activeTab === 'personal' ? (
            <>
              {/* Avatar Section */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Avatar
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-20 h-20 rounded-full bg-gray-200">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="Avatar"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <i className="fas fa-user text-5xl text-gray-400"></i>
                    )}
                  </div>
                  {isEditing && (
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        className="!rounded-button whitespace-nowrap px-5 py-2.5 bg-[#dadada] text-gray-700 text-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center gap-2 rounded-md"
                        onClick={() => setShowUploadModal(true)}
                      >
                        Upload Avatar
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {/* Role Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <div className="w-96 py-3 text-sm text-gray-700">
                  {formData.role}
                </div>
              </div>
              {/* Full Name Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Full name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-96 px-4 py-3 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 text-sm"
                  disabled={!isEditing}
                />
              </div>
              {/* Email Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>

                <div className="w-96 py-3 text-sm text-gray-700">
                  {formData.email}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Current Password Field */}
              <div className="space-y-2">
                {/* <label className="block text-sm font-medium text-gray-700">
                  Current Password
                </label> */}
                <input
                  type="password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  className="w-96 px-4 py-3 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 text-sm"
                  placeholder="Current Password"
                />
              </div>
              {/* New Password Field */}
              <div className="space-y-2">
                {/* <label className="block text-sm font-medium text-gray-700">
                  New Password
                </label> */}
                <input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className="w-96 px-4 py-3 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 text-sm"
                  placeholder="New Password"
                />
              </div>
              {/* Confirm Password Field */}
              <div className="space-y-2">
                {/* <label className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label> */}
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-96 px-4 py-3 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 text-sm"
                  placeholder="Confirm New Password"
                />
              </div>
            </>
          )}
          {/* Submit Button */}
          <div className="flex gap-4">
            {isEditing && activeTab === 'personal' && (
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="rounded whitespace-nowrap px-8 py-3 bg-gray-100 text-gray-700 border border-gray-300 font-medium text-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="rounded whitespace-nowrap px-8 py-3 bg-red-600 text-white font-medium text-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              {activeTab === 'personal'
                ? isEditing
                  ? 'Save'
                  : 'Edit'
                : 'Confirm Change'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Profile() {
  const { isGuest, user } = useContext(AuthContext);

  const searchParams = new URLSearchParams(window.location.search);
  const initialTab =
    searchParams.get('tab') === 'password' ? 'password' : 'personal';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(user.avatarPath);

  const [formData, setFormData] = useState({
    id: user.id,
    role: `I am a ${user.role}`,
    fullName: `${user.fullName}`,
    email: `${user.email}`,
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (activeTab === 'personal') {
      setIsEditing(!isEditing);
      if (isEditing) {
        // Save the changes here
        try {
          const formDataForUpdate = new FormData();

          formDataForUpdate.append('id', formData.id);
          formDataForUpdate.append('full_name', formData.fullName);

          if (avatarUrl) {
            const response = await fetch(avatarUrl);
            const blob = await response.blob();

            formDataForUpdate.append('avatar', blob);
          }

          const url = `${finalUrl}users/${formData.id}`;
          const response = await axios.put(url, formDataForUpdate);

          if (response.status === 200) {
            alert('User information updated successfully');
            setIsEditing(false);
            window.location.reload();
          } else {
            alert('Failed to update user information');
          }
        } catch (error) {
          console.error('Error updating user information: ', error);
          if (error.response) {
            const errorData = error.response.data;
            alert(errorData.error || 'Failed to update user information');
          } else if (error.request) {
            alert('No response from the server');
          } else {
            alert('Error setting up the request');
          }
        }
      } else {
        setIsEditing(true);
      }
    } else if (activeTab === 'password') {
      try {
        if (formData.newPassword !== formData.confirmPassword) {
          alert('New password and confirm password do not match');
          return;
        }
        const response = await axios.put(
          `${finalUrl}users/${formData.id}/password`,
          {
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword,
          },
        );
        if (response.status === 200) {
          alert('Password updated successfully');
          setFormData((prev) => ({
            ...prev,
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          }));
        } else {
          alert('Failed to update password');
        }
      } catch (error) {
        console.error('Error updating password: ', error);
        if (error.response) {
          const errorData = error.response.data;
          alert(errorData.error || 'Failed to update password');
        } else if (error.request) {
          alert('No response from the server');
        } else {
          alert('Error setting up the request');
        }
      }
    }
  };

  return !isGuest ? (
    <div className="min-h-screen bg-gray-100">
      {showUploadModal && (
        <UploadModal
          setShowUploadModal={setShowUploadModal}
          previewUrl={previewUrl}
          setPreviewUrl={setPreviewUrl}
          setAvatarUrl={setAvatarUrl}
        />
      )}

      <div className="flex items-center justify-center py-12">
        <div className="w-[1100px] flex gap-8">
          <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
          <Content
            activeTab={activeTab}
            handleSubmit={handleSubmit}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            setShowUploadModal={setShowUploadModal}
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
            avatarUrl={avatarUrl}
          />
        </div>
      </div>
    </div>
  ) : (
    <Navigate to="/login" />
  );
}
